import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { EntityMeta } from './types';

// Module-level cache so multiple forms sharing an entity don't refetch.
// Maps entity name → in-flight promise (deduplicates concurrent requests) and
// entity name → resolved result (serves subsequent mounts synchronously).
const promiseCache = new Map<string, Promise<EntityMeta>>();
const resultCache = new Map<string, EntityMeta>();

function fetchMeta(entity: string): Promise<EntityMeta> {
  const cached = promiseCache.get(entity);
  if (cached) return cached;

  const promise = api.meta
    .get(entity)
    .then((res) => {
      const meta = res.data as EntityMeta;
      resultCache.set(entity, meta);
      return meta;
    })
    .catch((err) => {
      // Drop the failed promise so a later mount can retry.
      promiseCache.delete(entity);
      throw err;
    });

  promiseCache.set(entity, promise);
  return promise;
}

interface UseEntityMetaResult {
  meta: EntityMeta | null;
  loading: boolean;
  error: string | null;
}

/**
 * Fetch (and cache) the resolved field metadata for an entity from
 * GET /api/meta/:entity. Driven by the schema-form generator.
 */
export function useEntityMeta(entity: string): UseEntityMetaResult {
  const [meta, setMeta] = useState<EntityMeta | null>(() => resultCache.get(entity) ?? null);
  const [loading, setLoading] = useState<boolean>(() => !resultCache.has(entity));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const existing = resultCache.get(entity);
    if (existing) {
      setMeta(existing);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    fetchMeta(entity)
      .then((result) => {
        if (cancelled) return;
        setMeta(result);
        setLoading(false);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError('Failed to load form metadata');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [entity]);

  return { meta, loading, error };
}
